/**
 * @file services/companies.js
 * @since v1.0
 * @author Carlos Lenon <code@carloslenon.com>
 */

// #region constant
// #endregion
// #region common
import { ApiResponse } from 'common/types/response';
import BaseServiceRetryWrapper from './baseRetry';
import { CAPTABLE } from '../common/endpoints';
// #endregion

class CapatableService extends BaseServiceRetryWrapper {
  /**
   * @function
   * @name dataById
   * @description load captable by id selected.
   * @param {INTEGER} dataId the captable id
   */
  dataById = (dataId: string) => this.get(`${CAPTABLE.byId}/${dataId}`);

  /**
   * @function
   * @name getCaptableById
   * @description load captable by id selected.
   * @param {integer} captableId the captable id
   */
  getCaptableById = <T = any>(captableId: number) =>
    this.get(CAPTABLE.captableById(captableId)) as Promise<ApiResponse<T>>;

  /**
   * @function
   * @name updateCaptableById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateCaptableById = (captableId: number, data: unknown) => this.put(CAPTABLE.captableById(captableId), data);

  /**
   * @function
   * @name updateSecuritiesById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateSecuritiesById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateSecurititesById(captableId), data);

  /**
   * @function
   * @name updateFundOwnershipById
   * @description update captable fund ownership by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateFundOwnershipById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateFundOwnershipById(captableId), data);

  createCapTableProforma = (data: unknown) => this.post(CAPTABLE.createProforma(), data);

  getPrimaryCaptableByCompanyDate = (companyDate: string) =>
    this.get(CAPTABLE.primaryCaptableByCompanyDate(companyDate));

  gotFinalAllocation = (id: number) => this.get(CAPTABLE.gotFinalAllocation(id));

  hasMeasurementDates = (id: number) => this.get(CAPTABLE.hasMeasurementDates(id));

  /**
   * @function
   * @name getAllBreakpoints
   * @description get all breakpoints (waterfall and custom)
   * @param {string} id the captable id
   */
  getAllBreakpointsData = (id: string) => this.get(CAPTABLE.allBreakpointsData(id));

  /**
   * @function
   * @name updateCustomBreakpoints
   * @description update custom breakpoints by cap table id.
   * @param {string} captableId the captable id
   */
  updateCustomBreakpoints = (captableId: string, data: unknown) =>
    this.put(CAPTABLE.allBreakpointsData(captableId), data);

  /**
   * @function
   * @name reviewCitation
   * @description accept, (soft, reversibly) dismiss, or reopen a single citation
   * reference (SCAL-8963/9262) - the citation-level settle for review-panel items
   * that carry no ai_finding field metadata to settle through. 'reopen' undoes
   * either decision back to pending.
   * @param {number} citationId the citation reference id
   * @param {string} action 'accept' | 'dismiss' | 'reopen'
   */
  reviewCitation = (citationId: number, action: 'accept' | 'dismiss' | 'reopen') =>
    this.patch(CAPTABLE.citationReviewById(citationId), { action });

  /**
   * @function
   * @name reviewMappings
   * @description accept, (soft, reversibly) dismiss, or reopen the AI Excel cell
   * mappings behind one review card (SCAL-9667). The card settles every one of its
   * AI mapping rows at once, and reviewing a mapping never changes a cell value.
   * @param {number[]} mappingIds the mapping reference ids to settle
   * @param {string} action 'accept' | 'dismiss' | 'reopen'
   */
  reviewMappings = (mappingIds: number[], action: 'accept' | 'dismiss' | 'reopen') =>
    this.patch(CAPTABLE.mappingReview, { mapping_ids: mappingIds, action });

  /**
   * @function
   * @name reopenAiFields
   * @description reopen settled (accepted/dismissed) AI review fields to pending
   * (SCAL-9262). Un-accept restores the value the accept wrote (previous_value
   * baseline) only while the cell still holds the AI value; restored_fields
   * echoes which requested fields were reverted, so the caller can flag cards
   * whose manual edit was kept instead.
   * @param {number} securityId the security id
   * @param {string[]} fieldNames the field names to reopen
   */
  reopenAiFields = (securityId: number, fieldNames: string[]) =>
    this.patch<{ reopened_fields: string[]; restored_fields: string[]; errors: string[] }>(
      CAPTABLE.reopenAiFieldsById(securityId),
      { field_names: fieldNames }
    );

  /**
   * @function
   * @name clearCapTable
   * @description delete every security on a cap table, with its values, AI
   * suggestions, citations and Excel references. Refused with a 409 when a
   * security is still used by fund ownership or a valuation.
   * @param {number} capTableId the cap table id
   */
  clearCapTable = (capTableId: number) => this.post<{ securities_deleted: number }>(CAPTABLE.clearById(capTableId));
}

export default CapatableService;
